const AUTH_DEBUG_STORAGE_KEY = "flymeta.auth_debug";
const AUTH_TELEMETRY_EVENT_NAME = "flymeta:auth-telemetry";
const AUTH_TAB_ID_STORAGE_KEY = "flymeta.auth.tab_id";

const SAFE_ID_PATTERN = /^[a-zA-Z0-9_.:-]+$/;

function canUseSessionStorage() {
    return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function createId(prefix) {
    const randomPart =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${prefix}.${randomPart}`;
}

export function getAuthTelemetryTabId() {
    if (!canUseSessionStorage()) {
        return createId("tab");
    }

    try {
        const existing = sessionStorage.getItem(AUTH_TAB_ID_STORAGE_KEY);
        if (existing && SAFE_ID_PATTERN.test(existing)) {
            return existing;
        }
        const next = createId("tab");
        sessionStorage.setItem(AUTH_TAB_ID_STORAGE_KEY, next);
        return next;
    } catch {
        return createId("tab");
    }
}

export function createAuthCorrelationId(entrypoint = "unknown") {
    const safeEntrypoint = String(entrypoint || "unknown").replace(/[^a-zA-Z0-9_.:-]/g, "_").slice(0, 48);
    return createId(`auth.${safeEntrypoint}`);
}

function shouldLogAuthDebug() {
    try {
        return localStorage.getItem(AUTH_DEBUG_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

function normalizeError(error) {
    if (!error) {
        return undefined;
    }

    return {
        name: error.name,
        message: error.message,
        code: error.code || error.authCode,
        httpStatus: error.response?.status || error.httpStatus,
        category: error.authCategory,
    };
}

function sanitizeDetail(detail) {
    if (!detail || typeof detail !== "object") {
        return detail || undefined;
    }

    const safeDetail = {};
    Object.entries(detail).forEach(([key, value]) => {
        const normalizedKey = String(key).toLowerCase();
        if (normalizedKey.includes("token") || normalizedKey.includes("authorization")) {
            return;
        }
        safeDetail[key] = value;
    });
    return safeDetail;
}

export function emitAuthTelemetry(event) {
    const safeEvent = {
        schema: "flymeta.auth.refresh.v1",
        timestamp: new Date().toISOString(),
        correlationId: event.correlationId || createAuthCorrelationId(event.entrypoint),
        entrypoint: event.entrypoint || "unknown",
        tabId: event.tabId || getAuthTelemetryTabId(),
        stage: event.stage || "unknown",
        outcome: event.outcome || "info",
        category: event.category || null,
        error: normalizeError(event.error),
        detail: sanitizeDetail(event.detail),
    };

    if (typeof window !== "undefined") {
        try {
            window.dispatchEvent(new CustomEvent(AUTH_TELEMETRY_EVENT_NAME, { detail: safeEvent }));
        } catch {
            // Telemetry must never affect authentication control flow.
        }
    }

    if (shouldLogAuthDebug()) {
        console.info("[auth-telemetry]", safeEvent);
    }

    const telemetryUrl = process.env.REACT_APP_AUTH_TELEMETRY_URL;
    if (!telemetryUrl || typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
        return safeEvent;
    }

    try {
        const payload = JSON.stringify(safeEvent);
        navigator.sendBeacon(telemetryUrl, new Blob([payload], { type: "application/json" }));
    } catch {
        // Telemetry delivery is best-effort by design.
    }

    return safeEvent;
}

export { AUTH_TELEMETRY_EVENT_NAME };
